package music.jammers.client.shared.event.presentation.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.event_create_applies
import limasoul.client.shared.generated.resources.event_create_button
import limasoul.client.shared.generated.resources.event_create_card_background
import limasoul.client.shared.generated.resources.event_create_date
import limasoul.client.shared.generated.resources.event_create_description
import limasoul.client.shared.generated.resources.event_create_genre
import limasoul.client.shared.generated.resources.event_create_limits
import limasoul.client.shared.generated.resources.event_create_members
import limasoul.client.shared.generated.resources.event_create_screen_title
import limasoul.client.shared.generated.resources.event_create_songs_count
import limasoul.client.shared.generated.resources.event_create_spot
import limasoul.client.shared.generated.resources.event_create_title
import music.jammers.client.shared.component.AddInstrumentImage
import music.jammers.client.shared.component.AppDateField
import music.jammers.client.shared.component.AppTextField
import music.jammers.client.shared.component.BackgroundBox
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.InstrumentImage
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.card.info.presentation.ui.CardInfoSpinnerComponent
import music.jammers.client.shared.event.di.EventComponent
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.presentation.viewmodel.CreateEventState
import music.jammers.client.shared.event.presentation.viewmodel.CreateEventViewModel
import music.jammers.shared.event.domain.model.EventInstrument
import music.jammers.shared.instrument.domain.model.InstrumentType
import org.jetbrains.compose.resources.stringResource

const val CREATE_EVENT_SCREEN = "CreateEventScreen"

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateEventScreen() {
    val viewModel: CreateEventViewModel = remember { EventComponent.createEventViewModel.apply { onReady() } }
    val state = viewModel.state.value
    val validationState = viewModel.validationState.value

    val sheetState = rememberModalBottomSheetState()

    val bottomSheetCoroutineScope = rememberCoroutineScope()
    bottomSheetCoroutineScope.launch {
        if (state.bottomNavigationOpen) {
            sheetState.show()
        } else {
            sheetState.hide()
        }
    }

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = stringResource(Res.string.event_create_screen_title),
                navigationIcon = {
                    IconButton(onClick = { viewModel.onBackPressed() }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back",
                        )
                    }
                },
            )
        },
    ) { paddingValues ->
        BackgroundBox {
            if (state.progress) {
                Progress()
            } else {
                Column(
                    modifier = Modifier.fillMaxSize().padding(paddingValues).verticalScroll(rememberScrollState())
                ) {
                    Content(
                        content = state,
                        validationState = validationState,
                        viewModel = viewModel,
                    )

                    if (sheetState.isVisible) {
                        ModalBottomSheet(
                            onDismissRequest = {
                                viewModel.onBottomSheetClosed()
                            },
                            content = {
                                SelectNewInstrument { value ->
                                    viewModel.onInstrumentAdded(value)
                                }
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun Content(
    content: CreateEventState,
    validationState: FieldValidationState,
    viewModel: CreateEventViewModel,
) {
    Column(modifier = Modifier.padding(32.dp)) {
        EventCommon(
            content = content,
            validationState = validationState,
            viewModel = viewModel,
        )

        EventLimits(
            content = content,
            validationState = validationState,
            viewModel = viewModel,
        )

        EventMembers(
            items = content.instruments,
            validationState = validationState,
            onItemDeleted = viewModel::onInstrumentDeleted,
            onItemAddClicked = viewModel::onInstrumentAddClicked,
            onItemClicked = viewModel::onInstrumentClicked,
        )

        Spacer(modifier = Modifier.size(8.dp))

        OutlinedButton(
            onClick = {
                viewModel.onCreateClicked()
            },
            enabled = validationState.errors.isEmpty(),
            modifier = Modifier
                .align(Alignment.CenterHorizontally),
        ) {
            Text(
                text = stringResource(Res.string.event_create_button),
                style = MaterialTheme.typography.headlineSmall,
                modifier = Modifier.padding(horizontal = 64.dp)
            )
        }
    }

    content.error?.let { value ->
        ErrorDialog(value.message ?: "Error") {
            viewModel.onErrorDialogClosed()
        }
    }
}

@Composable
private fun EventCommon(
    content: CreateEventState,
    validationState: FieldValidationState,
    viewModel: CreateEventViewModel,
) {
    val titleError = validationState.getValidationError(ValidateEventUseCase.TITLE).getString()
    AppTextField(
        value = content.title,
        placeholder = { Text(stringResource(Res.string.event_create_title).uppercase()) },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onTitleChanged(value)
        },
        errorText = titleError,
        modifier = Modifier.fillMaxWidth(),
    )

    val descriptionError = validationState.getValidationError(ValidateEventUseCase.DESCRIPTION).getString()
    AppTextField(
        value = content.description,
        placeholder = { Text(stringResource(Res.string.event_create_description).uppercase()) },
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onDescriptionChanged(value)
        },
        errorText = descriptionError,
        modifier = Modifier.fillMaxWidth(),
    )

    val cardInfoError = validationState.getValidationError(ValidateEventUseCase.CARD_INFO).getString()
    CardInfoSpinnerComponent(
        placeHolder = stringResource(Res.string.event_create_card_background).uppercase(),
        errorText = cardInfoError,
        modifier = Modifier.fillMaxWidth(),
    ) { value ->
        viewModel.onCardInfoChanged(value)
    }

    val dateError = validationState.getValidationError(ValidateEventUseCase.DATE).getString()
    AppDateField(
        localDateTime = content.date ?: Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()),
        title = stringResource(Res.string.event_create_date),
        errorText = dateError,
        modifier = Modifier.fillMaxWidth(),
    ) { selected ->
        viewModel.onDateChanged(selected)
    }

    val genreError = validationState.getValidationError(ValidateEventUseCase.GENRE).getString()
    AppTextField(
        value = content.genre,
        placeholder = { Text(stringResource(Res.string.event_create_genre).uppercase()) },
        errorText = genreError,
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Sentences,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next,
        ),
        onValueChange = { value ->
            viewModel.onGenreChanged(value)
        },
        modifier = Modifier.fillMaxWidth(),
    )

    val spotError = validationState.getValidationError(ValidateEventUseCase.SPOT).getString()
    AppTextField(
        value = content.spot,
        placeholder = { Text(stringResource(Res.string.event_create_spot).uppercase()) },
        errorText = spotError,
        keyboardOptions = KeyboardOptions(
            capitalization = KeyboardCapitalization.Words,
            autoCorrectEnabled = true,
            keyboardType = KeyboardType.Text,
            imeAction = ImeAction.Next
        ),
        onValueChange = { value ->
            viewModel.onSpotChanged(value)
        },
        modifier = Modifier.fillMaxWidth(),
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EventLimits(
    content: CreateEventState,
    validationState: FieldValidationState,
    viewModel: CreateEventViewModel,
) {
    Spacer(modifier = Modifier.size(8.dp))

    Text(
        text = stringResource(Res.string.event_create_limits).uppercase(),
        style = MaterialTheme.typography.headlineSmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth().padding(8.dp),
    )

    FlowRow(
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = stringResource(Res.string.event_create_songs_count).uppercase(),
            textAlign = TextAlign.Left,
            style = MaterialTheme.typography.titleSmall,
            modifier = Modifier.align(Alignment.CenterVertically)
        )

        val songCountError = validationState.getValidationError(ValidateEventUseCase.SONGS_COUNT)
        AppTextField(
            value = content.songsCount,
            errorText = songCountError.getString(),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Next
            ),
            onValueChange = { value ->
                viewModel.onSongsCountChanged(value)
            },
            modifier = Modifier.width(100.dp)
        )
    }

    Spacer(modifier = Modifier.size(8.dp))

    FlowRow(
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = stringResource(Res.string.event_create_applies).uppercase(),
            textAlign = TextAlign.Left,
            style = MaterialTheme.typography.titleSmall,
            modifier = Modifier.align(Alignment.CenterVertically)
        )

        val appliesPerPersonError = validationState.getValidationError(ValidateEventUseCase.APPLIES_PER_PERSON)
        AppTextField(
            value = content.appliesPerPerson,
            errorText = appliesPerPersonError.getString(),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.None,
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Next
            ),
            onValueChange = { value ->
                viewModel.onAppliesChanged(value)
            },
            modifier = Modifier.width(100.dp),
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EventMembers(
    items: List<EventInstrument>,
    validationState: FieldValidationState,
    onItemDeleted: (Int) -> Unit,
    onItemAddClicked: () -> Unit,
    onItemClicked: (Int) -> Unit,
) {
    Text(
        text = stringResource(Res.string.event_create_members).uppercase(),
        style = MaterialTheme.typography.headlineSmall,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth().padding(16.dp),
    )

    FlowRow(modifier = Modifier) {
        items.forEachIndexed { index, item ->

            InstrumentImage(
                eventInstrument = item,
                onClick = {
                    onItemClicked(index)
                },
                onDeleteClicked = {
                    onItemDeleted(index)
                },
            )

            Spacer(Modifier.size(8.dp))
        }

        AddInstrumentImage(
            onClick = onItemAddClicked,
        )

        Spacer(Modifier.size(8.dp))
    }

    val selectedInstrumentError = validationState.getValidationError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
        .getString()

    if (selectedInstrumentError == null) {
        Spacer(modifier = Modifier.size(16.dp))
    } else {
        Text(
            text = selectedInstrumentError,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1,
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SelectNewInstrument(onItemClicked: (InstrumentType) -> Unit) {
    Column(
        modifier = Modifier.fillMaxSize().padding(16.dp)
    ) {
        FlowRow(modifier = Modifier) {
            InstrumentType.entries.forEach { item ->
                InstrumentImage(
                    eventInstrument = EventInstrument(item, EventInstrument.Status.REQUIRED),
                    onClick = {
                        onItemClicked(item)
                    },
                )

                Spacer(Modifier.size(8.dp))
            }
        }
    }
}